import { useEffect, useMemo, useState } from 'react'
import { Property } from '@/custom-types';
import { PropertySimpleSelectionList } from './PropertySimpleSelectionList';
import { useCreateContractContext } from '@/customHooks/useCreateContractContext';
import { OrderDirectionOption, PageSizeOption, UnityStatusOption, useGetPropertiesForContractsQuery } from '@/graphql/generated';
import { useErrorHandlerContext, useUserLanguage } from '@/customHooks';
import { LoadingSpinner, RowSpacer, SquareButton2 } from '../atoms';
import { Col, Row } from 'react-grid-system';
import textsData from '@/textData/CreateContractsTexts.json'
import houseIcon from '@/assets/property-icons/house-icon.svg'
import { useLocation, useNavigate } from 'react-router';
import { pathURL } from '@/components/constants/urls';
import { ListFilterControls } from './ListFilterControls';
import { Pagination } from '../molecules';
import { UnityMultiSelectionList } from './UnityMultiSelectionList';

type SelectContractPropertyMenuProps = {
  onPropertySelected?: (propertyId: string | null, propertyName?: string | null) => void;
}

export const SelectContractPropertyMenu = ({ onPropertySelected }: SelectContractPropertyMenuProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es.selectProperty : textsData.en.selectProperty

  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null)
  const [selectedUnityIds, setSelectedUnityIds] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [orderDirection, setOrderDirection] = useState<OrderDirectionOption>(OrderDirectionOption.Asc)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = PageSizeOption.TwentyFive

  const { setProperty, setUnities, setButtonEnabled, setButtonDisabled } = useCreateContractContext()

  const location = useLocation()
  const locationState = location.state as { ownerId?: string } | null
  const ownerIdFromState = locationState?.ownerId

  const { open: errorHandle } = useErrorHandlerContext()

  const variables = useMemo(() => ({
    input: {
      search: search || undefined,
      orderDirection,
      page: currentPage.toString(),
      pageSize,
    }
  }), [orderDirection, search, currentPage, pageSize])

  useEffect(() => {
    setCurrentPage(1)
  }, [search, orderDirection])

  const { data, error, loading } = useGetPropertiesForContractsQuery({
    variables: variables,
    fetchPolicy: 'network-only'
  })

  const navigate = useNavigate()

  const properties = data?.properties?.items ?? []
  const filteredProperties = ownerIdFromState
    ? properties.filter((property) => property.owner?.user.id === ownerIdFromState)
    : properties

  useEffect(() => {
    if (selectedProperty) {
      setProperty(selectedProperty);
      setSelectedUnityIds([]);
      onPropertySelected?.(selectedProperty.id, selectedProperty.name);

      if (selectedProperty.isForRentWhole) {
        setButtonEnabled();
        setUnities([]);
      } else {
        setButtonDisabled();
      }
    } else {
      setButtonDisabled();
      onPropertySelected?.(null, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty]);

  useEffect(() => {
    if (selectedProperty && !selectedProperty.isForRentWhole) {
      if (selectedUnityIds.length > 0) {
        setButtonEnabled();
      } else {
        setButtonDisabled();
      }
      setUnities(selectedUnityIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnityIds, selectedProperty]);

  const propertyMapper = (properties: Property[]) => properties.map((property) => ({
    id: property.id,
    name: property.name,
    street: property.address?.street ?? '',
    city: property.address.city?.name ?? '',
    state: property.address.state?.name ?? '',
    status: property.occupancyStatusOption
  }))

  const onPropertySelectHandle = (proeprtyId: string) => {
    const property = filteredProperties.find((property) => property.id === proeprtyId)
    if (property) {
      setSelectedProperty(property)
    }
  }

  if (error) errorHandle(error)

  return (
    <>
      <Row justify='center'>
        <Col xs='content'>
          <h2>{texts.property}</h2>
        </Col>
      </Row>
      <Row justify='center'>
        <Col xs='content'>
          <ListFilterControls
            search={search}
            onSearchChange={setSearch}
            orderDirection={orderDirection}
            onOrderChange={setOrderDirection}
          />
        </Col>
      </Row>
      {loading ? (
        <Row>
          <Col>
            <LoadingSpinner />
          </Col>
        </Row>
      ) : (
        <>
          <Row justify='center'>
            <Col xs='content'>
              <PropertySimpleSelectionList
                selected={(property) => onPropertySelectHandle(property.id)}
                properties={propertyMapper(filteredProperties)}
                disabledOn={(property) => property.status === UnityStatusOption.NotAvailable || property.status === UnityStatusOption.Occupied}
              />
            </Col>
          </Row>
          {filteredProperties.length > 0 && (
            <Row justify='center'>
              <Col xs='content'>
                <Pagination
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  currentDataCount={filteredProperties.length}
                  pageSize={pageSize}
                  totalPages={data?.properties?.pagination?.totalPages}
                />
              </Col>
            </Row>
          )}
          <RowSpacer height={3} />
          {
            selectedProperty !== null && !selectedProperty.isForRentWhole
              ? (
                <Row justify='center' >
                  <Col xs='content' hidden={!selectedProperty}>
                    <h2>{texts.unity}</h2>
                    <UnityMultiSelectionList
                      selected={setSelectedUnityIds}
                      unities={selectedProperty?.unities ?? []}
                    />
                  </Col>
                </Row>
              )
              : (
                <Row justify='center'>
                  <Col xs='content'>
                    <span><b>{texts.rentWhole}</b></span>
                  </Col>
                </Row>
              )
          }
          <RowSpacer height={3} />
          <Row justify="center">
            <Col xs="content">
              <SquareButton2
                action={() => navigate(`/${pathURL.USER}/${pathURL.PROPERTY}/${pathURL.CREATE}`)}
              >
                <>
                  <img src={houseIcon} alt="" />
                  <span>{texts.addProperty}</span>
                </>
              </SquareButton2>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};
